/*
 * @providesModule SignupViewLegacy
 */

import React, {useState, useEffect} from 'react';
import {
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  BackHandler,
} from 'react-native';
import {_m} from 'INTL';
import styles from 'SignUpStyleLegacy';
import SkillzSVG from 'SkillzSVG';
import BannerModal from 'BannerModal';
import TextInputComponent from 'TextInputComponent';
import SkillzSDK from 'SkillzSDK';

const ERROR_CODE_EXISTING_IDENTITY_USER_ACCOUNT = 409;

interface SignUpProps {
  //Boolean for if federated login options should be shown
  showGoogleLogin?: boolean;
  showFacebookLogin?: boolean;
  showAppleLogin?: boolean;
  firstDeposit?: boolean;
  //can user exit screen
  allowExit: boolean;
  //can user to access sign in screen
  allowSignIn: boolean;
  //if after submit can pop back for navigation
  isAfterSubmitScore: boolean;
  //labels for inside username and password input
  usernameLabel?: string;
  passwordLabel?: string;
  //Forgot password click action
  signInPress: () => void;
  //Callback on “Close” button
  onClosePress: () => void;
  //Call back from android back button
  onBackButtonPress: () => void;
  //Handle callback on "Sign In" button
  onSignUpPress: (
    username: string,
    password: string,
    onDone?: (success: boolean) => void,
    tournament?: object,
    navigator?: object,
  ) => Promise<string>;
  //Handle callback on "Continue with Apple/facebook/google" button
  onContinueWithIDPPress: (
    fedType: string,
    onDone?: (success: boolean) => void,
    tournament?: object,
    navigator?: object,
  ) => Promise<number>;
  //These are to navigate user to certain screen or tournament after sign up
  completion?: () => {};
  //tournament needs navigator
  tournament?: object;
  navigator?: object;
  //method for initializing sdk
  notifySDK: (screenUp: boolean) => {};
  //User's email to pre-populate the field
  email?: string;
}

const TEST_IDS = {
  modalTestID: 'sign_up_modal',
  titleTestID: 'sign_up_title',
  secondHeader: 'sign_up_header',
  firstDepositTextTestID: 'sign_up_first_deposit_message',
  messageTestID: 'sign_up_message',
  inputUsernameTestID: 'sign_up_username_input',
  inputPasswordTestID: 'sign_up_password_input',
  signUpTestID: 'sign_up_button',
  haveAccountTestID: 'sign_up_have_account_message',
  signInTestID: 'sign_up_have_account_sign_in_button',
  closeTestID: 'sign_up_close',
  googleTestID: 'sign_up_google_sign_up_button',
  facebookTestID: 'sign_up_facebook_sign_up_button',
  appleTestID: 'sign_up_apple_sign_up_button',
  rewardsMessageTestID: 'sign_up_rewards_message',
  ribbonMessageTestID: 'sign_up_progress_message',
  customerSupportMessageTestID: 'sign_up_support_message',
};

const SignUpViewLegacy = (props: SignUpProps) => {
  const [password, setPassword] = useState<string>('');
  //True if password input is focus on for styling
  const [inputPasswordFocus, setPasswordFocus] = useState<boolean>(false);
  //if password input isn't acceptable
  const [inputPasswordError, setPasswordError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(props.email ?? '');
  //True if username/email input is focus on for styling
  const [inputUserNameFocus, setUserNameFocus] = useState<boolean>(false);
  //if email input isn't acceptable
  const [inputEmailError, setEmailError] = useState<boolean>(false);
  //Error Banner Visible
  const [errorBannerVisible, setErrorBannerVisible] = useState<boolean>(false);
  //Server Error Banner Visible
  const [serverErrorVisible, setServerErrorVisible] = useState<boolean>(false);
  //Boolean for if federated login options should be shown
  const showGoogleLogin = props.showGoogleLogin ?? false;
  const showFacebookLogin = props.showFacebookLogin ?? false;
  const showAppleLogin = props.showAppleLogin ?? false;

  const [signUpDisabled, setSignUpDisabled] = useState<boolean>(false);

  useEffect(() => {
    props.notifySDK(true);
    BackHandler.addEventListener('hardwareBackPress', () => {
      if (props.allowExit) {
        if (props.onBackButtonPress) {
          props.onBackButtonPress();
        }
        if (!props.isAfterSubmitScore) {
          props.onClosePress();
        }
      }
      return true;
    });
    return () => {
      props.notifySDK(false);
      BackHandler.removeEventListener('hardwareBackPress', () => undefined);
    };
  }, []);

  const resetErrors = (inputField: string) => {
    if (inputEmailError && inputField.match('email')) {
      setEmailError(false);
    }
    if (inputPasswordError && inputField.match('password')) {
      setPasswordError(false);
    }
  };

  const renderCloseBtn = () => {
    if (props.allowExit) {
      return (
        <TouchableOpacity
          testID={TEST_IDS.closeTestID}
          style={styles.closeBtn}
          onPress={props.onClosePress}
        >
          <SkillzSVG source={'close.svg'} style={styles.closeIcons} />
        </TouchableOpacity>
      );
    }
    return null;
  };

  const renderTitle = () => {
    return (
      <View style={styles.headerContainer}>
        <Text style={styles.headerText} testID={TEST_IDS.titleTestID}>
          {_m('Save Account')}
        </Text>
        {renderCloseBtn()}
      </View>
    );
  };

  const renderGoogleButton = () => {
    if (showGoogleLogin) {
      return (
        <TouchableOpacity
          style={styles.fedLoginBtn}
          onPress={() => handleFederatedSignUp('google')}
          testID={TEST_IDS.googleTestID}
        >
          <SkillzSVG source={'google.svg'} style={styles.googleIcon} />
          <Text style={styles.fedLoginText}>
            {_m('Continue with Google')}
          </Text>
        </TouchableOpacity>
      );
    }
    return null;
  };

  const renderFedLoginBtnSpacing = (shouldShow: boolean) => {
    if (shouldShow) {
      return <View style={styles.fedLoginBtnSpacing} />;
    }
    return null;
  };

  const renderFacebookButton = () => {
    if (showFacebookLogin) {
      return (
        <TouchableOpacity
          style={styles.fedLoginBtn}
          onPress={() => handleFederatedSignUp('facebook')}
          testID={TEST_IDS.facebookTestID}
        >
          <SkillzSVG source={'facebook.svg'} style={styles.facebookIcon} />
          <Text style={styles.fedLoginText}>
            {_m('Continue with Facebook')}
          </Text>
        </TouchableOpacity>
      );
    }
    return null;
  };

  const renderAppleButton = () => {
    if (showAppleLogin) {
      return (
        <TouchableOpacity
          style={styles.fedLoginBtn}
          onPress={() => handleFederatedSignUp('apple')}
          testID={TEST_IDS.appleTestID}
        >
          <SkillzSVG source={'apple.svg'} style={styles.appleIcon} />
          <Text style={styles.fedLoginText}>
            {_m('Continue with Apple')}
          </Text>
        </TouchableOpacity>
      );
    }
    return null;
  };

  const renderFedLoginButtons = () => {
    return (
      <View style={styles.fedLoginBtnContainer}>
        {renderFacebookButton()}
        {renderFedLoginBtnSpacing(showAppleLogin || showGoogleLogin)}
        {renderAppleButton()}
        {renderFedLoginBtnSpacing(showGoogleLogin)}
        {renderGoogleButton()}
      </View>
    );
  };

  const renderOrLine = () => {
    return (
      <View style={styles.orContainer}>
        <View style={styles.fedDivider} />
        <Text style={styles.orText}>
          {_m('OR')}
        </Text>
        <View style={styles.fedDivider1} />
      </View>
    );
  };

  const renderFederatedSignUpView = () => {
    if (showAppleLogin || showFacebookLogin || showGoogleLogin) {
      return (
        <>
          {renderFedLoginButtons()}
          {renderOrLine()}
        </>
      );
    }
    return null;
  };

  const passwordInputCheck = (pass: string) => {
    if (pass.length < 6) {
      setPasswordError(true);
      return false;
    } else {
      setPasswordError(false);
      return true;
    }
  };

  const passwordError = () => {
    if (inputPasswordError) {
      return (
        <View style={styles.errorContainer}>
          <SkillzSVG style={styles.errorIcon} source={'info_error.svg'} />
          <Text style={styles.errorMessage}>
            {_m('Must be at least 6 characters')}
          </Text>
        </View>
      );
    }
    return null;
  };

  const emailInputCheck = (username: string) => {
    const reg = /^\w+([+]?[\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w\w+)+$/;
    if (!reg.test(username)) {
      setEmailError(true);
      return false;
    } else {
      setEmailError(false);
      return true;
    }
  };

  const emailError = () => {
    if (inputEmailError) {
      return (
        <View style={styles.errorContainer}>
          <SkillzSVG style={styles.errorIcon} source={'info_error.svg'} />
          <Text style={styles.errorMessage}>
            {_m('Invalid email address')}
          </Text>
        </View>
      );
    }
    return null;
  };

  const renderPasswordTextField = () => {
    return (
      <View
        style={
          inputPasswordError
            ? styles.inputError
            : inputPasswordFocus
              ? styles.inputHighlight
              : styles.inputView
        }
      >
        <TextInputComponent
          autoCorrect={false}
          testID={TEST_IDS.inputPasswordTestID}
          accessibilityLabel={TEST_IDS.inputPasswordTestID}
          label={_m('Password')}
          value={password}
          onChangeText={value => setPassword(value)}
          style={styles.textInputPassword}
          showIcon={true}
          secureTextEntry
          inputStyle={styles.textInInput}
          keyboardType={'default'}
          onFocus={() => {
            setPasswordFocus(true);
            resetErrors('password');
          }}
          onBlur={() => {
            setPasswordFocus(false);
          }}
          labelStyle={
            inputPasswordError
              ? styles.labelTextError
              : inputPasswordFocus
                ? styles.labelTextHighlight
                : styles.labelText
          }
          iconStyle={styles.passwordIcon}
          placeholderStyle={styles.placeHolderText}
        />
      </View>
    );
  };

  const renderUsernameTextField = () => {
    return (
      <View
        style={
          inputEmailError
            ? styles.inputError
            : inputUserNameFocus
              ? styles.inputHighlight
              : styles.inputView
        }
      >
        <TextInputComponent
          autoCorrect={false}
          testID={TEST_IDS.inputUsernameTestID}
          accessibilityLabel={TEST_IDS.inputUsernameTestID}
          label={_m('Email address')}
          value={email}
          showIcon={false}
          onChangeText={value => setEmail(value)}
          style={styles.textInputEmail}
          inputStyle={styles.textInInput}
          onFocus={() => {
            setUserNameFocus(true);
            resetErrors('email');
          }}
          onBlur={() => setUserNameFocus(false)}
          placeholderStyle={styles.placeHolderText}
          keyboardType={'email-address'}
          labelStyle={
            inputEmailError
              ? styles.labelTextError
              : inputUserNameFocus
                ? styles.labelTextHighlight
                : styles.labelText
          }
        />
      </View>
    );
  };

  const renderSkillzSignUpHeader = () => {
    if (showAppleLogin || showFacebookLogin || showGoogleLogin) {
      return (
        <View style={styles.skillzSignUpTextContainer}>
          <Text style={styles.skillzSignUpText} testID={TEST_IDS.secondHeader}>
            {_m('Continue with Email')}
          </Text>
          <SkillzSVG
            style={styles.skillzLogo}
            source={'skillz_logo_wordmark_dark.svg'}
            tintColor="#3A3A3A"
          />
        </View>
      );
    }
    return null;
  };

  const handleSignUpPress = async () => {
    const checkEmail = emailInputCheck(email);
    const checkPassword = passwordInputCheck(password);
    if (checkEmail && checkPassword && !signUpDisabled) {
      try {
        setSignUpDisabled(true);
        const error = await props.onSignUpPress(
          email,
          password,
          props.completion,
          props.tournament,
          props.navigator,
        );
        if (error) {
          setSignUpDisabled(false);
          if (error === 'SIGN_UP_EMAIL_EXISTING_ACCOUNT' || error === 'EMAIL_ALREADY_EXISTS') {
            setErrorBannerVisible(true);
          } else {
            setServerErrorVisible(true);
          }
        } else {
          SkillzSDK.resetToHomeScreen();
          console.warn('SignUpViewLegacy: onSignUpPress returned success');
        }
      } catch (error) {
        setSignUpDisabled(false);
        console.warn('Error in login press', error);
      }
    }
  };

  const renderSignUpBtn = () => {
    return (
      <TouchableOpacity
        onPress={handleSignUpPress}
        testID={TEST_IDS.signUpTestID}
        style={styles.loginBtn}
      >
        <Text style={styles.loginText}>{_m('Sign Up')}</Text>
      </TouchableOpacity>
    );
  };

  const renderSkillzSignUpView = () => {
    return (
      <>
        {renderSkillzSignUpHeader()}
        {renderUsernameTextField()}
        {emailError()}
        {renderPasswordTextField()}
        {passwordError()}
        {renderSignUpBtn()}
      </>
    );
  };

  const saveAccountMessage = () => {
    if (props.firstDeposit) {
      return (
        <View>
          <Text
            style={styles.firstDepostText}
            testID={TEST_IDS.firstDepositTextTestID}
          >
            Congratulations on your first deposit!
          </Text>
          <Text
            style={styles.saveAccountToText}
            testID={TEST_IDS.messageTestID}
          >
            Save your account to
          </Text>
        </View>
      );
    }
    return (
      <Text style={styles.saveAccountText} testID={TEST_IDS.messageTestID}>
        {_m('Save your account!')}
      </Text>
    );
  };

  const saveAccountBackgroundImage = () => {
    return null;
    // These two .svg files are broken as they include base64 encoded png
    // data inside of them. We should replace them with proper .svg files,
    // or we need to migrate them to be real png files.
    // return (
    //   <SkillzSVG
    //     style={styles.background}
    //     source={isTablet() ? 'background_tablet.svg' : 'background.svg'}
    //   />
    // );
  };

  const rewardIcon = () => {
    return (
      <View style={styles.saveAccountIconContainer}>
        <SkillzSVG source={'rewards.svg'} style={styles.rewardsIcon} />
        <Text style={styles.imageText} testID={TEST_IDS.rewardsMessageTestID}>
          {_m('Earn rewards')}
        </Text>
      </View>
    );
  };

  const progressIcon = () => {
    return (
      <View style={styles.saveAccountIconContainer}>
        <SkillzSVG source={'ribbon.svg'} style={styles.ribbonIcon} />
        <Text style={styles.imageText} testID={TEST_IDS.ribbonMessageTestID}>
          {_m('Save your progress')}
        </Text>
      </View>
    );
  };

  const supportIcon = () => {
    return (
      <View style={styles.saveAccountIconContainer}>
        <SkillzSVG
          source={'customer_support.svg'}
          style={styles.customerSupportIcon}
        />
        <Text
          style={styles.imageText}
          testID={TEST_IDS.customerSupportMessageTestID}
        >
          {_m('Access support')}
        </Text>
      </View>
    );
  };

  const saveAccountBackground = () => {
    return (
      <>
        {saveAccountBackgroundImage()}
        {saveAccountMessage()}
        <View style={styles.saveAccountContainer}>
          {rewardIcon()}
          {progressIcon()}
          {supportIcon()}
        </View>
      </>
    );
  };

  const handleFederatedSignUp = async (fedType: string) => {
    try {
      const error = await props.onContinueWithIDPPress(
        fedType,
        props.completion,
        props.tournament,
        props.navigator,
      );
      if (error === ERROR_CODE_EXISTING_IDENTITY_USER_ACCOUNT) {
        setErrorBannerVisible(true);
      } else if (error) {
        setServerErrorVisible(true);
      }
    } catch (error) {
      console.warn('Error in federated login press', error);
    }
  };

  const renderErrorBanner = () => {
    if (errorBannerVisible) {
      return (
        <BannerModal
          title={_m('Account already exists')}
          message={_m('This email address already exists.')}
          numberOfLines={2}
          error={true}
          heightContainer={90}
          onClosePress={() => setErrorBannerVisible(false)}
        />
      );
    }

    if (serverErrorVisible) {
      return (
        <BannerModal
          title={_m('Server error')}
          message={_m('Sorry, there’s an issue with our sign up service. Please try again later.')}
          numberOfLines={2}
          error={true}
          heightContainer={90}
          onClosePress={() => setServerErrorVisible(false)}
        />
      );
    }
    return false;
  };

  const renderSignInButton = () => {
    if (props.allowSignIn) {
      return (
        <View style={styles.haveAccountContainer}>
          <Text
            style={styles.haveAccountText}
            testID={TEST_IDS.haveAccountTestID}
          >
            {_m('Already have an account?') + ' '}
          </Text>
          <TouchableOpacity
            onPress={() => {
              props.signInPress();
            }}
          >
            <Text style={styles.signInBtn} testID={TEST_IDS.signInTestID}>
              {_m('Sign In')}
            </Text>
          </TouchableOpacity>
        </View>
      );
    }
    return false;
  };

  return (
    <View style={styles.container} testID={TEST_IDS.modalTestID}>
      {renderTitle()}
      <ScrollView
        style={styles.containerScrollview}
        keyboardShouldPersistTaps={'always'}
        showsVerticalScrollIndicator={false}
        horizontal={false}
      >
        <KeyboardAvoidingView behavior="position" testID={TEST_IDS.modalTestID}>
          {saveAccountBackground()}
          <View style={{position: 'absolute'}}>{renderErrorBanner()}</View>
          {renderFederatedSignUpView()}
          {renderSkillzSignUpView()}
          {renderSignInButton()}
        </KeyboardAvoidingView>
      </ScrollView>
    </View>
  );
};

export default SignUpViewLegacy;
